/**
 * Dangerous Goods (DG) — shared cross-stack types.
 *
 * The UN substance system (numbers, hazard classes, packing groups) is globally
 * harmonized; these enums are regime-agnostic. Regime-specific behavior (ADR vs
 * 49 CFR HMR) is selected via `DangerousGoodsRegime` in country-config.types.ts.
 *
 * @see docs/planning/dangerous-goods/01-regime-abstraction.md
 */

// ─── Hazard classification (UN Model Regulations — harmonized) ──────────────

export enum HazardClass {
  CLASS_1_1 = '1.1',
  CLASS_1_2 = '1.2',
  CLASS_1_3 = '1.3',
  CLASS_1_4 = '1.4',
  CLASS_1_5 = '1.5',
  CLASS_1_6 = '1.6',
  CLASS_2_1 = '2.1', // flammable gas
  CLASS_2_2 = '2.2', // non-flammable, non-toxic gas
  CLASS_2_3 = '2.3', // toxic gas
  CLASS_3 = '3', // flammable liquid
  CLASS_4_1 = '4.1', // flammable solid
  CLASS_4_2 = '4.2', // spontaneously combustible
  CLASS_4_3 = '4.3', // dangerous when wet
  CLASS_5_1 = '5.1', // oxidizer
  CLASS_5_2 = '5.2', // organic peroxide
  CLASS_6_1 = '6.1', // toxic
  CLASS_6_2 = '6.2', // infectious
  CLASS_7 = '7', // radioactive
  CLASS_8 = '8', // corrosive
  CLASS_9 = '9', // miscellaneous
}

export enum PackingGroup {
  I = 'I',
  II = 'II',
  III = 'III',
}

// ─── ADR-specific (TR / EU regime) ───────────────────────────────────────────

/**
 * ADR tunnel restriction base letters. Substances carry composite codes like
 * `(D/E)` — first letter applies to tank carriage, second to packages; those
 * are stored verbatim on the substance and resolved to a base letter at
 * routing time. `E` is the most restrictive (no tunnels).
 */
export enum AdrTunnelCategory {
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
}

/** ADR Part 9 vehicle approval types. */
export enum AdrVehicleType {
  EX_II = 'EX_II', // explosives (Class 1)
  EX_III = 'EX_III', // explosives, higher spec
  FL = 'FL', // flammable liquids/gases tank vehicles
  AT = 'AT', // other tank vehicles
  MEMU = 'MEMU', // mobile explosives manufacturing unit
}

// ─── HMR-specific (US 49 CFR regime) ─────────────────────────────────────────

/** US cargo tank specifications (49 CFR 178 + HM-183 testing). */
export enum DotCargoTankSpec {
  DOT_406 = 'DOT_406', // flammable liquids (low pressure)
  DOT_407 = 'DOT_407', // chemicals (low-medium pressure)
  DOT_412 = 'DOT_412', // corrosives
  MC_331 = 'MC_331', // liquefied compressed gases
  MC_338 = 'MC_338', // cryogenic liquids
}

// ─── Compliance violations ───────────────────────────────────────────────────

/**
 * Violation codes emitted by DangerousGoodsComplianceService. Every violation
 * carries a regulation citation alongside the code (decision-support
 * positioning — explainable, never a bare rejection). Values reserved up
 * front to avoid enum churn (Decision #13); later phases light them up.
 */
export enum DGViolationCode {
  DRIVER_CERT_MISSING = 'DRIVER_CERT_MISSING',
  DRIVER_CERT_EXPIRED = 'DRIVER_CERT_EXPIRED',
  DRIVER_CLASS_TRAINING_MISSING = 'DRIVER_CLASS_TRAINING_MISSING',
  VEHICLE_TANK_CLASS_MISMATCH = 'VEHICLE_TANK_CLASS_MISMATCH',
  VEHICLE_APPROVAL_MISSING = 'VEHICLE_APPROVAL_MISSING',
  VEHICLE_APPROVAL_EXPIRED = 'VEHICLE_APPROVAL_EXPIRED',
  VEHICLE_EQUIPMENT_MISSING = 'VEHICLE_EQUIPMENT_MISSING',
  CARRIER_AUTHORITY_MISSING = 'CARRIER_AUTHORITY_MISSING',
  CARRIER_AUTHORITY_EXPIRED = 'CARRIER_AUTHORITY_EXPIRED',
  CARRIER_CLASS_NOT_ACCEPTED = 'CARRIER_CLASS_NOT_ACCEPTED',
  SEGREGATION_CONFLICT = 'SEGREGATION_CONFLICT',
  CAPACITY_EXCEEDED = 'CAPACITY_EXCEEDED',
  ROUTE_TUNNEL_RESTRICTED = 'ROUTE_TUNNEL_RESTRICTED',
  ROUTE_RESTRICTED = 'ROUTE_RESTRICTED',
  DOCUMENT_MISSING = 'DOCUMENT_MISSING',
}

export interface DGViolation {
  code: DGViolationCode;
  message: string;
  /** Regulation citation, e.g. "ADR 8.2.1" or "49 CFR 177.848". */
  citation: string;
  entityRef?: { type: string; id: string };
}

export interface DGComplianceResult {
  passed: boolean;
  violations: DGViolation[];
}

// ─── Vehicle DG profile (DG-8 #1595) ─────────────────────────────────────────

/**
 * 49 CFR 180.407 cargo-tank test dates (HM-183). Keys are the CFR test
 * codes: V external visual, I internal visual, P pressure, L lining,
 * T thickness, K leakage. ISO date strings (jsonb column).
 */
export interface Hm183Tests {
  V?: string;
  I?: string;
  P?: string;
  L?: string;
  T?: string;
  K?: string;
}

/**
 * On-vehicle dangerous-goods equipment checklist (jsonb, v1 — upgraded to
 * an inspection child table only if per-item history is demanded, Phase 7).
 * Shared by Trailer (load-side kit) and PowerUnit (cab kit + marking
 * holders); all fields optional so each side stores what applies.
 */
export interface DgEquipment {
  extinguishers?: { count: number; lastInspectionAt?: string };
  wheelChocks?: boolean;
  spillKit?: boolean;
  eyeRinse?: boolean;
  ppe?: boolean;
  groundingCable?: boolean;
  /** Orange-plate holder (ADR) / placard holder (HMR) present on the unit. */
  orangePlateHolder?: boolean;
  placardHolder?: boolean;
}

// ─── HERE Routing v8 mapping ─────────────────────────────────────────────────

/** Enumerated values HERE Routing v8 accepts for truck[shippedHazardousGoods]. */
export type HereShippedHazardousGood =
  | 'explosive'
  | 'gas'
  | 'flammable'
  | 'combustible'
  | 'organic'
  | 'poison'
  | 'radioactive'
  | 'corrosive'
  | 'poisonousInhalation'
  | 'harmfulToWater'
  | 'other';

/**
 * Hazard class → HERE shippedHazardousGoods value. Conservative first cut:
 * classes without a dedicated HERE value map to 'other' (still triggers
 * generic hazmat road restrictions). Environmentally-hazardous substances
 * additionally contribute 'harmfulToWater' (flag-driven, not class-driven).
 * Refined in Phase 5 (docs/planning/dangerous-goods/05-routing.md).
 */
export const HAZARD_CLASS_TO_HERE_HAZMAT: Record<HazardClass, HereShippedHazardousGood> = {
  [HazardClass.CLASS_1_1]: 'explosive',
  [HazardClass.CLASS_1_2]: 'explosive',
  [HazardClass.CLASS_1_3]: 'explosive',
  [HazardClass.CLASS_1_4]: 'explosive',
  [HazardClass.CLASS_1_5]: 'explosive',
  [HazardClass.CLASS_1_6]: 'explosive',
  [HazardClass.CLASS_2_1]: 'gas',
  [HazardClass.CLASS_2_2]: 'gas',
  [HazardClass.CLASS_2_3]: 'poisonousInhalation',
  [HazardClass.CLASS_3]: 'flammable',
  [HazardClass.CLASS_4_1]: 'other',
  [HazardClass.CLASS_4_2]: 'other',
  [HazardClass.CLASS_4_3]: 'other',
  [HazardClass.CLASS_5_1]: 'other',
  [HazardClass.CLASS_5_2]: 'organic',
  [HazardClass.CLASS_6_1]: 'poison',
  [HazardClass.CLASS_6_2]: 'other',
  [HazardClass.CLASS_7]: 'radioactive',
  [HazardClass.CLASS_8]: 'corrosive',
  [HazardClass.CLASS_9]: 'other',
};
